import { useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { opcInfoAtom } from '../../recoil/atoms/opcInfoAtom';
import { jobInfoAtom } from '../../recoil/atoms/jobInfoAtom';
import { callMeVariantOpcAtom } from '../../recoil/atoms/callMeVariantOpcAtom';
import { infoModalVariantOpcAtom } from '../../recoil/atoms/infoModalVariantOpcAtom';
import { isOpcFetchingAtom } from '../../recoil/atoms/isOpcFetchingAtom';
import { useSaveCoverableInformation } from '../../hooks/useSaveCoverableInformation';
import { useOPCCallMeModal } from '../../hooks/useOPCCallMeModal';
import { useOPCInfoModal } from '../../hooks/useOPCInfoModal';
import useOpcJob from '../../hooks/useOpcJob';
import ExistingCoverage from './components/ExistingCoverage/ExistingCoverage';
import PetHealthyLastSixMonthsOPC from './components/PetHealthyLastSixMonthsOPC';
import OpcModal from './components/OpcModal';
import PrhEditAttributePage from './components/PrhEditAttributePage';
import ExistingShoppingBasketModal from '../../shared_components/ExistingShoppingBasketModal/ExistingShoppingBasketModal';
import CallMeModal from '../../shared_components/CallMeModal/CallMeModal';
import ViewInformationModal from '../../shared_components/ViewInformationModal/ViewInformationModal';
import { isPrivateHouse } from './utils/editAttributeUtil';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import type { OpcEntryPageRoute, ActionType, HandleCloseOPCModal } from './types';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import {
    PET_HEALTH_COVERAGE,
    PET_HEALTH_PLUS_COVERAGE,
    PET_LIFE_COVERAGE
} from '../../constants/coverage';
import { opcOldPriceAtom } from '../../recoil/atoms/opcOldPriceAtom';

const OpcEntryPage: WizardPageComponent = () => {
    const [opcInfo, setOpcInfo] = useRecoilState(opcInfoAtom);
    const { tilpas, productName, attribute, adjustAction, isFromDAFlow, coverageId } = opcInfo;
    const { isExistingBasket, jobNumber, sessionId } = useRecoilValue(jobInfoAtom);

    const [isOpcModalOpen, setIsOpcModalOpen] = useState(true);
    const petHealthRelatedCoverages = [
        PET_HEALTH_COVERAGE,
        PET_HEALTH_PLUS_COVERAGE,
        PET_LIFE_COVERAGE
    ];
    const [shouldPromptForPetHealth, setShouldPromptForPetHealth] = useState(
        petHealthRelatedCoverages.includes(coverageId ?? '') &&
            (productName === 'topdanmark_dog' || productName === 'topdanmark_cat')
    );
    const [showExistingShoppingBasketModal, setShowExistingShoppingBasketModal] = useState(
        isExistingBasket && tilpas !== 'tilpas'
    );
    const [existingBasket] = useState(isExistingBasket ? { jobNumber, sessionId } : undefined);

    const callMeVariant = useRecoilValue(callMeVariantOpcAtom);
    const [informationModalVariant, setInformationModalVariant] =
        useRecoilState(infoModalVariantOpcAtom);
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);

    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { withdrawOPCShoppingBasket } = useOpcJob();
    const { callMeProps } = useOPCCallMeModal(() => {
        setIsOpcModalOpen(true);
    });
    const resetOpcOldPrice = useResetRecoilState(opcOldPriceAtom);
    const { infoProps } = useOPCInfoModal();

    const petHealthInfoRef = useRef<CoverableInformationToUpdateDTO>();

    const getPage = (): OpcEntryPageRoute => {
        if (shouldPromptForPetHealth && !isFromDAFlow) {
            return 'PetHealthyLastSixMonthsOPC';
        }

        if (callMeVariant) {
            return 'CallMeModal';
        }

        if (informationModalVariant) {
            return 'ViewInformationModal';
        }

        if (showExistingShoppingBasketModal && existingBasket) {
            return 'ExistingShoppingBasketModal';
        }

        if (tilpas === 'attribut' && !attribute && isPrivateHouse(productName)) {
            return 'PrhEditAttributePage';
        }

        if (tilpas === 'tilpas' && !adjustAction) {
            return 'ExistingCoverage';
        }

        if (isOpcModalOpen) {
            return 'OpcModal';
        }
    };

    const handleCloseModal: HandleCloseOPCModal = (
        closeCallback?: () => Promise<void> | void,
        actionType?: ActionType
    ): void => {
        if (!closeCallback) {
            setIsOpcModalOpen(false);
            return;
        }
        resetOpcOldPrice();

        if (
            actionType === 'goToShoppingBasket' ||
            actionType === 'goToCheckoutPage' ||
            actionType === 'goToReceiptPage'
        ) {
            if (petHealthInfoRef.current) {
                void saveCoverableInformation(petHealthInfoRef.current);
            }
            if (existingBasket) {
                void withdrawOPCShoppingBasket(existingBasket.jobNumber, existingBasket.sessionId);
            }
            setIsOpcFetching(true);
            /* Flos dialog modal is out of control of react suspense
                closeCallback block UI re-render so isOpcFetching === false until closeCallback done
                queueMicrotask help to queue closeCallback until UI re-render with isOpcFetching === true */
            queueMicrotask(() => {
                void closeCallback();
            });
            return;
        }

        const closeCallbackExecute = closeCallback();
        if (closeCallbackExecute instanceof Promise) {
            closeCallbackExecute
                .then(() => {
                    setIsOpcModalOpen(false);
                })
                .catch((e) => {
                    throw e;
                });
        } else {
            setIsOpcModalOpen(false);
        }
        setOpcInfo((preInfo) => ({
            ...preInfo,
            adjustAction: undefined
        }));
    };

    const handleOpenModal = (): void => {
        if (existingBasket?.jobNumber) {
            setShowExistingShoppingBasketModal(true);
        } else {
            setIsOpcModalOpen(true);
        }
    };

    switch (getPage()) {
        case 'PetHealthyLastSixMonthsOPC':
            return (
                <PetHealthyLastSixMonthsOPC
                    continueToOpc={(petHealthInfo) => {
                        setShouldPromptForPetHealth(false);
                        petHealthInfoRef.current = petHealthInfo;
                    }}
                />
            );
        case 'CallMeModal':
            if (callMeProps) {
                return <CallMeModal {...callMeProps} />;
            }
            break;
        case 'ViewInformationModal':
            if (infoProps) {
                return (
                    <ViewInformationModal
                        showModal={!!informationModalVariant}
                        modalProps={infoProps}
                        closeModal={() => {
                            setInformationModalVariant(undefined);
                            setIsOpcModalOpen(true);
                        }}
                    />
                );
            }
            break;
        case 'ExistingShoppingBasketModal':
            if (existingBasket) {
                return (
                    <ExistingShoppingBasketModal
                        existingBasket={existingBasket}
                        showModal={showExistingShoppingBasketModal}
                        closeModal={(shouldOpcModalOpen) => {
                            setShowExistingShoppingBasketModal(false);
                            setIsOpcModalOpen(shouldOpcModalOpen);
                        }}
                    />
                );
            }
            break;
        case 'PrhEditAttributePage':
            return <PrhEditAttributePage setIsOpenModal={setIsOpcModalOpen} />;
        case 'ExistingCoverage':
            return <ExistingCoverage onOpenModal={handleOpenModal} />;
        case 'OpcModal':
            return (
                <OpcModal
                    modal={tilpas}
                    handleCloseModal={handleCloseModal}
                    shouldNavigateToShoppingBasket={!!adjustAction}
                />
            );
    }
    return <></>;
};

export default OpcEntryPage;
